import client from '../../magpiie-client/client';
import type { AppDispatch, RootState } from '../store';
import {
  login,
  updateToken,
  fetchUserRequest,
  fetchUserFailure,
  logout,
  initializeAuthComplete,
} from '../types';
import * as tokenManager from '../../services/tokenManager';
import * as authService from '../../services/authService';
import { fetchOrganizations } from './organizations';
import { fetchChannelsForOrg } from './channels';
import { fetchCurrentSubscription } from './subscription';

// Module-level guard to prevent duplicate initialization calls
let isInitializationInProgress = false;

/**
 * Initialize auth on app load by attempting to refresh the token.
 * If refresh succeeds, fetches user profile and logs in.
 * If refresh fails, user remains unauthenticated.
 */
export function initializeAuthFromRefresh() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    // Prevent duplicate initialization (e.g., React 18 StrictMode double effects)
    if (isInitializationInProgress) {
      return;
    }
    isInitializationInProgress = true;

    try {
      // Check if we have a stored refresh token
      const storedRefreshToken = tokenManager.getRefreshToken();
      if (!storedRefreshToken) {
        // No refresh token - user needs to log in
        // Don't dispatch here - let finally block handle it
        return;
      }

      // Attempt to refresh using the stored refresh token
      const accessToken = await tokenManager.refresh();

      if (accessToken) {
        dispatch(updateToken(accessToken));

        // Fetch user profile
        const { data, error } = await client.GET('/auth/user');

        if (data && !error) {
          dispatch(login(data));

          // Fetch organizations after successful login
          await dispatch(fetchOrganizations());

          // Get active organization and fetch its channels (only if ACCEPTED)
          const state = getState();
          const activeOrgId = state.organizations.activeOrganizationId;
          const activeOrg = activeOrgId
            ? state.organizations.organizations[activeOrgId.toString()]
            : undefined;

          if (activeOrgId && activeOrg?.invitation_status === 'ACCEPTED') {
            await dispatch(fetchChannelsForOrg(activeOrgId));
            dispatch(fetchCurrentSubscription(activeOrgId));
          }
        } else {
          // Token valid but user fetch failed - clear state
          tokenManager.clearToken();
          dispatch(logout());
        }
      }
    } catch {
      // Refresh failed - user is not authenticated
      // This is expected for first-time visitors or expired sessions
      tokenManager.clearToken();
    } finally {
      isInitializationInProgress = false;
      dispatch(initializeAuthComplete());
    }
  };
}

/**
 * Login with email/password using session-based authentication.
 * Stores refresh token in localStorage, access token in memory.
 */
export function loginWithSessionAction(
  email: string,
  password: string,
  recaptchaToken: string
) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(fetchUserRequest());

    // Login and get tokens
    const data = await authService.loginWithSession(
      email,
      password,
      recaptchaToken
    );
    tokenManager.setToken(data.access_token, data.expires_in);
    if (data.refresh_token) {
      tokenManager.setRefreshToken(data.refresh_token);
    }
    dispatch(updateToken(data.access_token));

    // Fetch user profile
    const { data: userData, error } = await client.GET('/auth/user');

    if (error) {
      tokenManager.clearToken();
      dispatch(updateToken(''));
      const message = (error as any)?.message || 'Failed to fetch user';
      dispatch(fetchUserFailure(String(message)));
      dispatch(logout());
      throw error;
    }

    if (userData) {
      dispatch(login(userData));

      // Fetch organizations after successful login
      await dispatch(fetchOrganizations());

      // Get active organization and fetch its channels (only if ACCEPTED)
      const state = getState();
      const activeOrgId = state.organizations.activeOrganizationId;
      const activeOrg = activeOrgId
        ? state.organizations.organizations[activeOrgId.toString()]
        : undefined;

      if (activeOrgId && activeOrg?.invitation_status === 'ACCEPTED') {
        await dispatch(fetchChannelsForOrg(activeOrgId));
        dispatch(fetchCurrentSubscription(activeOrgId));
      }
    }

    // Mark initialization complete so ProtectedRoute doesn't re-trigger auth
    dispatch(initializeAuthComplete());
  };
}

/**
 * Google OAuth login with session-based authentication.
 * Exchanges Google auth code for session tokens.
 */
export function googleAuthWithSessionAction(code: string) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(fetchUserRequest());

    // Exchange Google code for tokens and user data
    const data = await authService.googleAuthWithSession(code);
    tokenManager.setToken(data.access_token, data.expires_in);
    if (data.refresh_token) {
      tokenManager.setRefreshToken(data.refresh_token);
    }
    dispatch(updateToken(data.access_token));

    // Use user data from response (fallback mode) or fetch it
    let userData = data.user;

    if (!userData) {
      // Session endpoint returned tokens only, fetch user separately
      const { data: fetchedUser, error } = await client.GET('/auth/user');

      if (error) {
        tokenManager.clearToken();
        dispatch(updateToken(''));
        const message = (error as any)?.message || 'Failed to fetch user';
        dispatch(fetchUserFailure(String(message)));
        dispatch(logout());
        throw error;
      }

      userData = fetchedUser;
    }

    if (userData) {
      dispatch(login(userData));

      // Fetch organizations after successful login
      await dispatch(fetchOrganizations());

      // Get active organization and fetch its channels (only if ACCEPTED)
      const state = getState();
      const activeOrgId = state.organizations.activeOrganizationId;
      const activeOrg = activeOrgId
        ? state.organizations.organizations[activeOrgId.toString()]
        : undefined;

      if (activeOrgId && activeOrg?.invitation_status === 'ACCEPTED') {
        await dispatch(fetchChannelsForOrg(activeOrgId));
        dispatch(fetchCurrentSubscription(activeOrgId));
      }
    }

    // Mark initialization complete so ProtectedRoute doesn't re-trigger auth
    dispatch(initializeAuthComplete());
  };
}

/**
 * Logout from current session.
 * Calls API to revoke session, clears local token state.
 */
export function logoutWithSession() {
  return async (dispatch: AppDispatch) => {
    try {
      await authService.logout();
    } catch (error) {
      // Log but don't block - still clear local state
      console.warn('Logout API call failed:', error);
    }

    tokenManager.clearToken();
    dispatch(logout());
  };
}

/**
 * Legacy login with access token.
 * Now stores token in memory via tokenManager instead of localStorage.
 * Kept for backwards compatibility during migration.
 *
 * @deprecated Use loginWithSessionAction for new implementations
 */
export function loginWithAccessToken(accessToken: string) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    // Store token in memory (no longer in localStorage)
    tokenManager.setToken(accessToken, 900); // Default 15 min expiry
    dispatch(updateToken(accessToken));

    // Fetch current user using the API client
    dispatch(fetchUserRequest());
    const { data, error } = await client.GET('/auth/user');

    if (error) {
      tokenManager.clearToken();
      dispatch(updateToken(''));
      const message = (error as any)?.message || 'Failed to fetch user';
      dispatch(fetchUserFailure(String(message)));
      dispatch(logout());
      throw error;
    }

    if (data) {
      dispatch(login(data));

      // Fetch organizations after successful login
      await dispatch(fetchOrganizations());

      // Get active organization and fetch its channels (only if ACCEPTED)
      const state = getState();
      const activeOrgId = state.organizations.activeOrganizationId;
      const activeOrg = activeOrgId
        ? state.organizations.organizations[activeOrgId.toString()]
        : undefined;

      if (activeOrgId && activeOrg?.invitation_status === 'ACCEPTED') {
        await dispatch(fetchChannelsForOrg(activeOrgId));
        dispatch(fetchCurrentSubscription(activeOrgId));
      }
    }

    // Mark initialization complete so ProtectedRoute doesn't re-trigger auth
    dispatch(initializeAuthComplete());
  };
}

/**
 * Complete registration by storing tokens, user data, and fetching organizations.
 * Used after create-user-from-token returns access_token + refresh_token + user.
 */
export function completeRegistration(
  accessToken: string,
  refreshToken: string | undefined,
  expiresIn: number | undefined,
  user: any
) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    // Store access token in memory
    tokenManager.setToken(accessToken, expiresIn || 900);
    dispatch(updateToken(accessToken));

    // Store refresh token if provided (for session-based auth)
    if (refreshToken) {
      tokenManager.setRefreshToken(refreshToken);
    }

    // Dispatch login with user data (no need to fetch again)
    dispatch(login(user));

    // Fetch organizations after successful login
    await dispatch(fetchOrganizations());

    // Get active organization and fetch its channels (only if ACCEPTED)
    const state = getState();
    const activeOrgId = state.organizations.activeOrganizationId;
    const activeOrg = activeOrgId
      ? state.organizations.organizations[activeOrgId.toString()]
      : undefined;

    if (activeOrgId && activeOrg?.invitation_status === 'ACCEPTED') {
      await dispatch(fetchChannelsForOrg(activeOrgId));
      dispatch(fetchCurrentSubscription(activeOrgId));
    }

    // Mark initialization complete so ProtectedRoute doesn't re-trigger auth
    dispatch(initializeAuthComplete());
  };
}

// Re-export for convenience and compatibility
export { login, googleAuthLogin, updateToken, logout } from '../types';
